import { wgResource, wgResourceType } from "./wgResource.js";
import { wMath } from "wFundament/wMath.js"

const wgTextureFiltering = {
    TFNearest: 0,
    TFLinear: 1,
    TFMipMapping: 2
}

const wgTextureWrapping = {
    TWRepeat: 0,
    TWClampToEdge: 1,
    TWMirroredRepeat: 2,
    TWClmapToBorder: 3
}

const WEBGL_PIXEL_FORMAT = {
	DEPTH_COMPONENT: 0x1902,
	DEPTH_STENCIL: 0x84F9,
	ALPHA: 0x1906,
	RED: 0x1903, // webgl2
	RGB: 0x1907,
	RGBA: 0x1908,
	LUMINANCE: 0x1909,
	LUMINANCE_ALPHA: 0x190A,
	// only for internal formats
	R8: 0x8229, // webgl2
	RGBA8: 0x8058,
	RGBA16F: 0x881A,
	RGBA32F: 0x8814,
	DEPTH_COMPONENT16: 0x81A5,
	DEPTH_COMPONENT24: 0x81A6,
	DEPTH_COMPONENT32F: 0x8CAC,
	DEPTH24_STENCIL8: 0x88F0,
	DEPTH32F_STENCIL8: 0x8CAD
}

const WEBGL_PIXEL_TYPE = {
	BYTE: 0x1400,
	UNSIGNED_BYTE: 0x1401,
	SHORT: 0x1402,
	UNSIGNED_SHORT: 0x1403,
	INT: 0x1404,
	UNSIGNED_INT: 0x1405,
	FLOAT: 0x1406,
	HALF_FLOAT: 36193,
	UNSIGNED_INT_24_8: 0x84FA,
	UNSIGNED_SHORT_4_4_4_4:	0x8033,
	UNSIGNED_SHORT_5_5_5_1: 0x8034,
	UNSIGNED_SHORT_5_6_5: 0x8363,
	FLOAT_32_UNSIGNED_INT_24_8_REV: 0x8DAD
}

class wgTexture extends wgResource {
    constructor(name, context) {
        super(name, context, wgResourceType.wgTexture);
        this.width_ = 0;
        this.height_ = 0;
        this.depth_ = 0;
        this.activeTexture_ = null;
        this.textureHandle_ = null;
        this.textureType_ = null;
        this.textureFormat_ = null;
        this.textureDataType_ = null;
        this.textureFiltering_ = wgTextureFiltering.TFNearest;

        this.textureWrapS_ = wgTextureWrapping.TWClampToEdge;
        this.textureWrapT_ = wgTextureWrapping.TWClampToEdge;
        this.textureWrapU_ = wgTextureWrapping.TWClampToEdge;
    }

    getTextureHandle() {
        return this.textureHandle_;
    }
    getWidth() {
        return this.width_;
    }
    getHeight() {
        return this.height_;
    }
    getDepth() {
        return this.depth_;
    }

    setActiveTexture(activeTextureChannel){
        this.activeTexture_ = activeTextureChannel;
    }

    createTextureHandle(textureType) {
        const gl = this.getContext().gl();
        let validTextureType = false;

        // Note that gl.TEXTURE_1D is not part of webgl1+2.
        if (textureType === gl.TEXTURE_2D) {
            validTextureType = true;
        }

        if (textureType === gl.TEXTURE_CUBE_MAP) {
            validTextureType = true;
        }

        if (textureType === gl.TEXTURE_2D_ARRAY) {
            validTextureType = true;
        }

        if (textureType === gl.TEXTURE_3D) {
            validTextureType = true;
        }

        if (!validTextureType) {
            throw Error("Invalid texture type");
        }

        this.activeTexture_ = gl.TEXTURE0;
        this.textureType_ = textureType;
        this.textureHandle_ = gl.createTexture();
    }

    deleteTexture() {
        if (this.textureHandle_ !== null) {
            const gl = this.getContext().gl();
            gl.deleteTexture(this.textureHandle_);
            this.textureHandle_ = null;
        }
    }

    resize2D(width, height) {
        if (width !== this.getWidth() || height !== this.getHeight()) {
            this.deleteTexture();
            const tfNearest = this.textureFiltering_ == wgTextureFiltering.TFNearest;
            const tfMipMap = this.textureFiltering_ == wgTextureFiltering.TFMipMapping;
            this.create2D(this.textureFormat_, width, height, tfNearest, tfMipMap);
        }
    }

    bind() {
        this.getContext().gl().activeTexture(this.activeTexture_);
        this.getContext().gl().bindTexture(this.textureType_, this.getTextureHandle());
    }

    unbind() {
        this.getContext().gl().activeTexture(this.activeTexture_);
        this.getContext().gl().bindTexture(this.textureType_, null);
    }

    setTextureWrappingRepeat() {
        const gl = this.getContext().gl();
        this.bind();
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
        this.unbind();
    }

    createFromHandle(glTextureHandle, width, height){
        const gl = this.getContext().gl();
        this.textureHandle_  = glTextureHandle;
        this.width_ = width;
        this.height_ = height; 
        this.activeTexture_ = gl.TEXTURE0;
        this.textureType_ = gl.TEXTURE_2D;
    }

    create2DArray(internalFormat, width, height, layers, nearestfiltering) {
        const gl = this.getContext().gl();
        if (this.getTextureHandle() !== null) {
            //throw Error("texture handle already created");
            gl.deleteTexture(this.getTextureHandle());
        }

        this.createTextureHandle(gl.TEXTURE_2D_ARRAY);
        if (this.textureType_ !== gl.TEXTURE_2D_ARRAY) {
            throw Error("invalid texture type");
        }

        // this.textureDataType_ = dataType;
        this.textureFormat_ = internalFormat;
        this.width_ = width;
        this.height_ = height;
        this.depth_ = layers;
        this.bind();

        gl.texStorage3D(gl.TEXTURE_2D_ARRAY, 1, internalFormat, this.width_, this.height_, this.depth_);
        gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

        if (nearestfiltering) {
            gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
            gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
            this.textureFiltering_ = wgTextureFiltering.TFNearest;
        } else {
            gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
            this.textureFiltering_ = wgTextureFiltering.TFLinear;
        }
        this.unbind();
    }

    create2D(internalFormat, width, height, nearestfiltering, createMipMap) {
        const gl = this.getContext().gl();
        if (this.getTextureHandle() !== null) {
            //throw Error("texture handle already created");
            gl.deleteTexture(this.getTextureHandle());
        }

        this.createTextureHandle(gl.TEXTURE_2D);
        if (this.textureType_ !== gl.TEXTURE_2D) {
            throw Error("invalid texture type");
        }

        // this.textureDataType_ = dataType;
        this.textureFormat_ = internalFormat;
        this.width_ = width;
        this.height_ = height;
        this.bind();

        gl.texStorage2D(gl.TEXTURE_2D, 1, internalFormat, this.width_, this.height_);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

        if (createMipMap !== undefined && createMipMap && wMath.isPowerOfTwo(this.width_) && wMath.isPowerOfTwo(this.height_)) {
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
            gl.generateMipmap(gl.TEXTURE_2D);
            this.textureFiltering_ = wgTextureFiltering.TFMipMapping;
        } else if (nearestfiltering) {
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
            this.textureFiltering_ = wgTextureFiltering.TFNearest;
        } else {
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
            this.textureFiltering_ = wgTextureFiltering.TFLinear;
        }
        this.unbind();
    }

    create2dFromImage(image, imageFormat, internalFormat, dataType, nearestfiltering, createMipMap) {
        const gl = this.getContext().gl();

        if (this.getTextureHandle() !== null) {
            throw Error("texture handle already created");
        }

        this.createTextureHandle(gl.TEXTURE_2D);
        if (this.textureType_ !== gl.TEXTURE_2D) {
            throw Error("invalid texture type");
        }

        this.textureDataType_ = dataType;
        this.textureFormat_ = internalFormat;
        this.width_ = image.width;
        this.height_ = image.height;

        this.bind();
        gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, imageFormat, dataType, image);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

        if (createMipMap !== undefined && createMipMap && wMath.isPowerOfTwo(this.width_) && wMath.isPowerOfTwo(this.height_)) {
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
            gl.generateMipmap(gl.TEXTURE_2D);
            this.textureFiltering_ = wgTextureFiltering.TFMipMapping;
        } else if (nearestfiltering) {
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
            this.textureFiltering_ = wgTextureFiltering.TFNearest;
        } else {
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
            this.textureFiltering_ = wgTextureFiltering.TFLinear;
        }

        this.unbind();
    }

    createShadowMapDepthTexture(width, height) {
        const gl = this.getContext().gl();

        if (this.getTextureHandle() !== null) {
            gl.deleteTexture(this.getTextureHandle());
        }

        this.createTextureHandle(gl.TEXTURE_2D);
        if (this.textureType_ !== gl.TEXTURE_2D) {
            throw Error("invalid texture type");
        }

        this.width_ = width;
        this.height_ = height;
        this.bind();

        gl.texImage2D(gl.TEXTURE_2D, 0, gl.DEPTH_COMPONENT32F, this.width_, this.height_, 0, gl.DEPTH_COMPONENT, gl.FLOAT, null);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

        // To get shadowSampler2d to work we need to magic formulas
     //   gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      //  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);

        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_COMPARE_FUNC, gl.LEQUAL);
        //gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_COMPARE_FUNC, gl.LESS);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_COMPARE_MODE, gl.COMPARE_REF_TO_TEXTURE);

        this.textureFiltering_ = wgTextureFiltering.TFLinear;// TFNearest;
     
        this.unbind();
    }

    uploadDataToTexture(dataFormat, dataType, data) {
        const gl = this.getContext().gl();
        this.bind();
        if ( this.textureType_ == gl.TEXTURE_2D )
        {
            gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, this.width_, this.height_, dataFormat, dataType, data);
        }
        else if ( this.textureType_ == gl.TEXTURE_2D_ARRAY || this.textureType_ == gl.TEXTURE_3D )
        {
            gl.texSubImage3D(this.textureType_, 0, 0, 0, 0, this.width_, this.height_, this.depth_, dataFormat, dataType, data);
        }

        this.unbind();
    }

    // create 3d TODO
}

export { wgTexture, wgTextureFiltering, wgTextureWrapping };
